import { Location } from "@angular/common";
import { Component, OnInit, ChangeDetectorRef, AfterViewInit, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { BaseComponent, UserService, ProductService, SimpleProduct, AdminService, WindowService, ContentService, BaseContent } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { AddEditProductContentComponent, GenericAlertComponent } from "../../modals";
import { ProductContentService } from "../../../services/product-content.service";
import { finalize } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";

interface ContentPackage extends BaseContent {
  content_package_ids: string[];
  product_ids: string[] | number[];
  total_records: number;
  is_majority: boolean;

  // Processed properties for UI
  displayMetadata?: Array<{ title: string; value: string; color?: string }>;
  href?: string; // Added for navigation
}

// Generic filter interface
interface FilterOptions {
  query?: string;
  type?: string;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  [key: string]: any;
}

// Generic sort options
interface SortOptions {
  field: string;
  direction: "asc" | "desc";
}

// Generic filter configuration interface
interface FilterConfig {
  key: string;
  label: string;
  type: "select" | "radio" | "checkbox";
  options: FilterOption[];
  defaultValue?: string;
  disabledWhen?: string; // key of filter that disables this one
  onChange?: string; // method name to call on change
  // Pre-computed template binding properties
  disabled?: boolean;
  filterSelected?: boolean;
}

interface FilterOption {
  value: string;
  label: string;
  disabled?: boolean;
}

// Sort configuration interface
interface SortConfig {
  key: string;
  label: string;
  options: SortOption[];
  defaultValue?: string;
}

interface SortOption {
  value: string;
  label: string;
  icon?: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-content-packages-manager",
  templateUrl: "content-packages-manager.component.html",
  standalone: false,
})
export class ContentPackagesManagerComponent extends BaseComponent implements OnInit, AfterViewInit {
  // Loading states
  public loading: boolean = false;
  public contentLoading: boolean = false;

  // Data arrays
  public products: Array<SimpleProduct> = [];
  public contentPackages: ContentPackage[] = [];
  public filteredContentPackages: ContentPackage[] = [];
  public displayedContentPackages: ContentPackage[] = [];

  // Pagination
  public page: number = 0;
  public limit: number = 100;
  public totalPages: number = 0;
  public totalItems: number = 0;
  public pageBeforeSearch: number = 0;

  // Caching
  public cachedContentPackages: ContentPackage[] = [];
  public cachedProducts: Record<string, SimpleProduct[]> = {
    software: [],
    hardware: [],
    extensions: [],
  };

  // Filter and search
  public filtering: boolean = false;
  public filterOptions: any = {};

  // Generic filter configuration
  public filterConfigs: FilterConfig[] = [];

  public sortConfigs: SortConfig[] = [];

  // Pre-computed sort configs for template bindings
  public sortByConfig: SortConfig | undefined;
  public sortDirectionConfig: SortConfig | undefined;

  // Unified filter state
  public filters: FilterOptions = {
    sortBy: "date",
    sortDirection: "desc",
    type: "any",
    query: "",
    beta: "any",
    downloadSection: "any",
    folder: "any",
    versionType: "any",
  };

  constructor(
    userService: UserService,
    public productService: ProductService,
    public adminService: AdminService,
    public appService: AppService,
    public contentService: ContentService,
    public router: Router,
    public location: Location,
    private cdr: ChangeDetectorRef,
    private productContentService: ProductContentService,
    public translate: TranslateService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.initializeFilterConfigs();
    this.initializeFilters();
    this.loadInitialData();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.cdr.detectChanges();
    });
  }

  // ===== INITIALIZATION METHODS =====

  private initializeFilterConfigs(): void {
    this.filterConfigs = [
      {
        key: "type",
        label: this.translate.instant("content_packages.type_label"),
        type: "select",
        options: [
          { value: "any", label: this.translate.instant("general.all_types") },
          { value: "firmware", label: this.translate.instant("general.firmware") },
          { value: "content", label: this.translate.instant("general.content") },
        ],
        defaultValue: "any",
        onChange: "onTypeFilterChange",
      },
      {
        key: "downloadSection",
        label: this.translate.instant("content_packages.download_section"),
        type: "select",
        options: [
          { value: "any", label: this.translate.instant("general.all_sections") },
          { value: "no_download", label: this.translate.instant("download_sections.no_download") },
          { value: "demos_tutorials", label: this.translate.instant("download_sections.demos_tutorials") },
          { value: "installers", label: this.translate.instant("download_sections.installers") },
          { value: "misc", label: this.translate.instant("download_sections.misc") },
          { value: "owners_manuals", label: this.translate.instant("download_sections.owners_manuals") },
          { value: "presets", label: this.translate.instant("download_sections.presets") },
          { value: "release_notes", label: this.translate.instant("download_sections.release_notes") },
          { value: "soundsets", label: this.translate.instant("download_sections.soundsets") },
        ],
        defaultValue: "any",
        onChange: "onDownloadSectionFilterChange",
      },
      {
        key: "versionType",
        label: this.translate.instant("content_packages.version_type_label"),
        type: "select",
        options: [
          { value: "any", label: this.translate.instant("general.all_versions") },
          { value: "major", label: this.translate.instant("general.major_releases") },
          { value: "minor", label: this.translate.instant("general.minor_releases") },
          { value: "patch", label: this.translate.instant("general.patch_releases") },
        ],
        defaultValue: "any",
        onChange: "onVersionTypeFilterChange",
      },
    ];

    this.sortConfigs = [
      {
        key: "sortBy",
        label: this.translate.instant("content_packages.sort_by_label"),
        options: [
          { value: "date", label: this.translate.instant("general.date") },
          { value: "title", label: this.translate.instant("general.title") },
        ],
        defaultValue: "date",
      },
      {
        key: "sortDirection",
        label: this.translate.instant("content_packages.sort_direction_label"),
        options: [
          { value: "desc", label: this.translate.instant("general.descending"), icon: "arrow-down" },
          { value: "asc", label: this.translate.instant("general.ascending"), icon: "arrow-up" },
        ],
        defaultValue: "desc",
      },
    ];

    // Pre-compute sort config references for template
    this.sortByConfig = this.sortConfigs.find((c) => c.key === "sortBy");
    this.sortDirectionConfig = this.sortConfigs.find((c) => c.key === "sortDirection");
  }

  private updateFilterConfigStates(): void {
    this.filterConfigs.forEach((config) => {
      config.disabled = config.disabledWhen ? !!this.filters[config.disabledWhen] : false;
      config.filterSelected = this.filters[config.key] != null && this.filters[config.key] !== "any";
    });
  }

  private initializeFilters(): void {
    this.appService.preferredSortDirection = "asc";

    // Initialize filters from configuration
    this.initializeFiltersFromConfig();

    // Sync with app service settings
    this.filters.sortBy = this.appService.sortBy || "date";
    this.filters.sortDirection = (this.appService.sortDirection as "asc" | "desc") || "desc";

    // Apply any provided filter options
    if (this.filterOptions) {
      this.filters = { ...this.filters, ...this.filterOptions };
    }

    this.updateFilterConfigStates();
  }

  private initializeFiltersFromConfig(): void {
    // Initialize filter values from configuration
    this.filterConfigs.forEach((config) => {
      if (config.defaultValue && !this.filters[config.key]) {
        this.filters[config.key] = config.defaultValue;
      }
    });

    // Initialize sort values from configuration
    this.sortConfigs.forEach((config) => {
      if (config.defaultValue && !this.filters[config.key]) {
        this.filters[config.key] = config.defaultValue;
      }
    });
  }

  private loadInitialData(): void {
    Promise.all([this.getProducts("software"), this.getProducts("hardware"), this.getProducts("extensions")]);
    this.loadContentPackages();
  }

  private applyTextFilter<T extends BaseContent>(items: T[], query: string): T[] {
    if (!query) return items;

    const lowerQuery = query.toLowerCase();
    return items.filter(
      (item) =>
        item.title.toLowerCase().includes(lowerQuery) ||
        item.description?.toLowerCase().includes(lowerQuery) ||
        item.version?.toLowerCase().includes(lowerQuery) ||
        item.fileName?.toLowerCase().includes(lowerQuery) ||
        item.stringId?.toLowerCase().includes(lowerQuery) ||
        item.folder?.toLowerCase().includes(lowerQuery) ||
        item.downloadSection?.toLowerCase().includes(lowerQuery) ||
        item.type?.toLowerCase().includes(lowerQuery)
    );
  }

  private applyTypeFilter<T extends BaseContent>(items: T[], type: string): T[] {
    if (!type || type === "any") return items;

    return items.filter((item) => item.type?.toLowerCase() === type.toLowerCase());
  }

  private applyBetaFilter<T extends BaseContent>(items: T[], betaFilter: string): T[] {
    if (!betaFilter || betaFilter === "any") return items;

    return items.filter((item) => {
      const isBeta = this.isBetaValue(item.beta);
      return betaFilter === "true" ? isBeta : !isBeta;
    });
  }

  private applyDownloadSectionFilter<T extends BaseContent>(items: T[], downloadSection: string): T[] {
    if (!downloadSection || downloadSection === "any") return items;

    return items.filter((item) => item.downloadSection?.toLowerCase() === downloadSection.toLowerCase());
  }

  private applyFolderFilter<T extends BaseContent>(items: T[], folder: string): T[] {
    if (!folder || folder === "any") return items;

    return items.filter((item) => item.folder?.toLowerCase() === folder.toLowerCase());
  }

  private applyVersionTypeFilter<T extends BaseContent>(items: T[], versionType: string): T[] {
    if (!versionType || versionType === "any") return items;

    return items.filter((item) => {
      if (!item.version) return false;

      const versionParts = item.version.split(".");
      if (versionParts.length < 2) return false;

      const major = parseInt(versionParts[0]) || 0;
      const minor = parseInt(versionParts[1]) || 0;
      const patch = parseInt(versionParts[2]) || 0;

      switch (versionType) {
        case "major":
          return minor === 0 && patch === 0;
        case "minor":
          return minor > 0 && patch === 0;
        case "patch":
          return patch > 0;
        default:
          return true;
      }
    });
  }

  private sortItems<T>(items: T[], options: SortOptions, getValue: (item: T, field: string) => any): T[] {
    return [...items].sort((a, b) => {
      const valueA = getValue(a, options.field);
      const valueB = getValue(b, options.field);

      if (options.field === "date") {
        const dateA = valueA ? new Date(valueA).getTime() : 0;
        const dateB = valueB ? new Date(valueB).getTime() : 0;
        return options.direction === "asc" ? dateA - dateB : dateB - dateA;
      }

      const comparison = valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
      return options.direction === "asc" ? comparison : -comparison;
    });
  }

  public getFilterConfig(key: string): FilterConfig | undefined {
    return this.filterConfigs.find((config) => config.key === key);
  }

  public getSortConfig(key: string): SortConfig | undefined {
    return this.sortConfigs.find((config) => config.key === key);
  }

  public isFilterDisabled(config: FilterConfig): boolean {
    if (!config.disabledWhen) return false;
    return !!this.filters[config.disabledWhen];
  }

  public onFilterChange(config: FilterConfig, value: string): void {
    this.filters[config.key] = value;
    this.page = 0;
    this.pageBeforeSearch = 0;
    this.updateFilterConfigStates();

    // Call specific onChange method if defined
    if (config.onChange && typeof this[config.onChange] === "function") {
      this[config.onChange]();
    } else {
      // Default filter behavior
      this.applyContentFilters();
    }
  }

  public onSortChange(config: SortConfig, value: string): void {
    this.filters[config.key] = value;

    // Call specific sort methods if they exist
    if (config.key === "sortBy") {
      this.onSortByChange(value);
    } else if (config.key === "sortDirection") {
      this.onSortDirectionChange(value as "asc" | "desc");
    } else {
      // Default filter behavior
      this.applyContentFilters();
    }
  }

  loadContentPackages(force?: boolean): void {
    if (!force && this.cachedContentPackages.length > 0) {
      this.setContentPackagesFromCache();
      return;
    }

    this.contentLoading = true;
    this.contentPackages = [];
    this.displayedContentPackages = [];

    this.adminService
      .get(`product/content-multiple`)
      .pipe(
        finalize(() => {
          this.contentLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (response: any) => {
          this.contentPackages = this.extractContentPackagesFromResponse(response);
          this.cachedContentPackages = [...this.contentPackages];
          this.prepareContentPackagesMetadata();
          this.applyContentFilters();
          this.contentLoading = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error("Error loading content packages:", error);
          this.appService.alertError(error);
          this.contentPackages = [];
          this.displayedContentPackages = [];
          this.cdr.detectChanges();
        },
      });
  }

  private extractContentPackagesFromResponse(response: any): ContentPackage[] {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (response?.data && Array.isArray(response.data)) return response.data;
    if (response?.results && Array.isArray(response.results)) return response.results;
    if (response?.contentPackages && Array.isArray(response.contentPackages)) return response.contentPackages;
    if (response?.content && Array.isArray(response.content)) return response.content;
    if (response?.items && Array.isArray(response.items)) return response.items;
    return [];
  }

  private setContentPackagesFromCache(): void {
    this.contentPackages = [...this.cachedContentPackages];
    this.displayedContentPackages = [...this.contentPackages];
    this.prepareContentPackagesMetadata();
    this.applyContentFilters();
  }

  private prepareContentPackagesMetadata(): void {
    this.contentPackages.forEach((content: ContentPackage) => {
      // Set fallback values
      content.title = content.title || this.translate.instant("general.untitled");
      content.version = content.version || "";
      content.description = content.description || "";
      content.releaseDate = content.releaseDate || "";
      content.type = content.type || "";
      content.beta = this.normalizeStringBeta(content.beta);

      content.href = "/products/content_packages/" + (content.content_package_ids?.[0] || "");

      content.displayMetadata = this.buildContentMetadata(content);
    });

    this.updateFilterOptions();
  }

  private updateFilterOptions(): void {
    // Update folder filter options
    const folderConfig = this.filterConfigs.find((config) => config.key === "folder");
    if (folderConfig) {
      const uniqueFolders = [...new Set(this.contentPackages.map((content) => content.folder).filter((folder) => folder && folder.trim() !== ""))].sort();

      folderConfig.options = [{ value: "any", label: this.translate.instant("content_packages.all_folders") }, ...uniqueFolders.map((folder) => ({ value: folder, label: folder }))];
    }
  }

  private buildContentMetadata(content: ContentPackage): Array<{ title: string; value: string; color?: string }> {
    const metadata = [];

    // Core properties
    if (content.title) metadata.push({ title: this.translate.instant("general.title"), value: content.title });
    if (content.version) metadata.push({ title: this.translate.instant("content_packages.version"), value: content.version });
    if (content.type) metadata.push({ title: this.translate.instant("content_packages.metadata_type"), value: content.type });
    if (content.DTYPE) metadata.push({ title: this.translate.instant("content_packages.metadata_dtype"), value: content.DTYPE });
    if (content.description) {
      const shortDesc = content.description.length > 50 ? content.description.substring(0, 50) + "..." : content.description;
      metadata.push({ title: this.translate.instant("general.description"), value: shortDesc });
    }
    if (content.releaseDate) {
      metadata.push({ title: this.translate.instant("content_packages.metadata_release_date"), value: new Date(content.releaseDate).toLocaleDateString() });
    }
    if (this.isBetaValue(content.beta)) {
      metadata.push({ title: this.translate.instant("content_packages.metadata_status"), value: this.translate.instant("general.beta"), color: "green" });
    }

    // File properties
    if (content.fileName) metadata.push({ title: this.translate.instant("content_packages.metadata_file"), value: content.fileName });
    if (content.mimeType) metadata.push({ title: this.translate.instant("content_packages.metadata_mime_type"), value: content.mimeType });
    if (content.contentLength) metadata.push({ title: this.translate.instant("content_packages.metadata_size"), value: content.contentLength });
    if (content.stringId) metadata.push({ title: this.translate.instant("content_packages.metadata_id"), value: content.stringId });

    // Organization properties
    if (content.folder) metadata.push({ title: this.translate.instant("content_packages.metadata_folder"), value: content.folder });
    if (content.downloadSection) metadata.push({ title: this.translate.instant("content_packages.metadata_download_section"), value: content.downloadSection });
    if (content.cdnUrl) metadata.push({ title: this.translate.instant("content_packages.metadata_cdn_url"), value: content.cdnUrl });

    // Technical properties
    if (content.osRequirement) metadata.push({ title: this.translate.instant("content_packages.metadata_os_requirement"), value: content.osRequirement });
    if (content.flags) metadata.push({ title: this.translate.instant("content_packages.metadata_flags"), value: content.flags });
    if (content.serialCutin) metadata.push({ title: this.translate.instant("content_packages.metadata_serial_cutin"), value: content.serialCutin });
    if (content.signature) metadata.push({ title: this.translate.instant("content_packages.metadata_signature"), value: content.signature });

    return metadata;
  }

  applyContentFilters(): void {
    let filtered = [...this.contentPackages];

    // Apply type filter
    filtered = this.applyTypeFilter(filtered, this.filters.type);

    // Apply beta status filter
    filtered = this.applyBetaFilter(filtered, this.filters.beta);

    // Apply download section filter
    filtered = this.applyDownloadSectionFilter(filtered, this.filters.downloadSection);

    // Apply folder filter
    filtered = this.applyFolderFilter(filtered, this.filters.folder);

    // Apply version type filter
    filtered = this.applyVersionTypeFilter(filtered, this.filters.versionType);

    // Apply text search
    filtered = this.applyTextFilter(filtered, this.filters.query);

    // Apply sorting
    this.filteredContentPackages = this.sortItems(
      filtered,
      {
        field: this.filters.sortBy,
        direction: this.filters.sortDirection,
      },
      (item: ContentPackage, field: string) => {
        if (field === "date") return item.releaseDate;
        return item[field as keyof BaseContent];
      }
    );

    this.updateDisplayedPage();
  }

  updateDisplayedPage(): void {
    this.totalItems = this.filteredContentPackages.length;
    this.totalPages = Math.max(Math.ceil(this.totalItems / this.limit) - 1, 0);

    // Clamp page to valid range
    if (this.page > this.totalPages) {
      this.page = this.totalPages;
    }

    const start = this.page * this.limit;
    this.displayedContentPackages = this.filteredContentPackages.slice(start, start + this.limit);
  }

  paginate(direction: string): void {
    this.page = this.page + (direction === "next" ? 1 : -1);
    this.updateDisplayedPage();
  }

  // ===== FILTER EVENT HANDLERS =====

  onSortByChange(field: string): void {
    this.filters.sortBy = field;
    this.appService.sortBy = field;
    this.applyContentFilters();
  }

  onSortDirectionChange(direction: "asc" | "desc"): void {
    this.filters.sortDirection = direction;
    this.appService.sortDirection = direction;
    this.applyContentFilters();
  }

  onTypeFilterChange(): void {
    this.applyContentFilters();
  }

  onBetaFilterChange(value: string): void {
    this.filters.beta = value;
    this.applyContentFilters();
  }

  onDownloadSectionFilterChange(): void {
    this.applyContentFilters();
  }

  onVersionTypeFilterChange(): void {
    this.applyContentFilters();
  }

  onSearchChange(query: string): void {
    const wasSearching = !!this.filters.query;
    this.filters.query = query;
    this.filtering = !!query;

    if (query && !wasSearching) {
      // Entering search: save current page
      this.pageBeforeSearch = this.page;
      this.page = 0;
    } else if (!query && wasSearching) {
      // Clearing search: restore previous page
      this.page = this.pageBeforeSearch;
    }

    this.applyContentFilters();
  }

  onClearFilters(): void {
    this.filters = {
      sortBy: "date",
      sortDirection: "desc" as "asc" | "desc",
      type: "any",
      query: "",
      beta: "any",
      downloadSection: "any",
      folder: "any",
      versionType: "any",
    };
    this.filtering = false;
    this.page = 0;
    this.pageBeforeSearch = 0;
    this.updateFilterConfigStates();
    this.applyContentFilters();
  }

  clearContentPackagesCache(): void {
    this.cachedContentPackages = [];
  }

  refreshContentPackages(): void {
    this.clearContentPackagesCache();
    this.loadContentPackages(true);
  }

  createContentPackage(): void {
    const createData = {
      type: this.filters.type === "any" ? "content" : this.filters.type,
      DTYPE: this.filters.type === "firmware" ? "Firmware" : "ContentPackage",
      downloadSection: this.filters.type === "firmware" ? "no_download" : "",
    };

    this.showContentPackageModal(createData, [], [], true);
  }

  editContentPackage(content: ContentPackage, event?: any): void {
    if (event) {
      event.stopPropagation();
    }
    const editData = this.mapContentToEditData(content);
    const { productId, selectedProductIds, preselectedProducts } = this.extractProductData(content);
    this.showContentPackageModal(editData, selectedProductIds, preselectedProducts, false);
  }

  private showContentPackageModal(data: any, selectedProductIds: number[], preselectedProducts: any[], isCreating: boolean): void {
    const modalRef = this.appService.showModal(AddEditProductContentComponent, { size: "lg" });

    // Set modal properties
    modalRef.componentInstance.data = data;
    modalRef.componentInstance.productId = undefined; // Set to undefined since we're using multiple product IDs
    modalRef.componentInstance.showProductSelection = true;
    modalRef.componentInstance.isContentPackagesManager = true;
    modalRef.componentInstance.isCreating = isCreating; // Set isCreating based on whether data has an id
    modalRef.componentInstance.selectedProductIds = selectedProductIds;

    if (preselectedProducts.length) {
      modalRef.componentInstance.selectedProducts = preselectedProducts;
    }

    modalRef.componentInstance.completed.subscribe({
      next: (result) => {
        if (result) {
          this.handleContentPackageSubmit(result);
        }
      },
    });
  }

  private mapContentToEditData(content: ContentPackage): any {
    return {
      id: content.content_package_ids?.[0],
      content_package_ids: content.content_package_ids || [],
      type: content.type || (this.filters.type === "any" ? "content" : this.filters.type),
      title: content.title || "",
      stringId: content.stringId || "",
      description: content.description || "",
      version: content.version || "",
      releaseDate: content.releaseDate || "",
      mimeType: content.mimeType || "",
      contentLength: content.contentLength ? parseInt(String(content.contentLength)) : undefined,
      osRequirement: content.osRequirement || "",
      downloadSection: content.downloadSection || "",
      cdnUrl: content.cdnUrl || "",
      beta: this.normalizeStringBeta(content.beta),
      folder: content.folder || "",
      flags: content.flags || "",
      serialCutin: content.serialCutin || "",
      signature: content.signature || "",
      fileName: content.fileName,
      DTYPE: content.DTYPE,
    };
  }

  private extractProductData(content: ContentPackage): {
    productId: number;
    selectedProductIds: number[];
    preselectedProducts: any[];
  } {
    const productId = content.product_ids?.[0] ? parseInt(String(content.product_ids[0])) : 1;
    const selectedProductIds = (content.product_ids || []).map((id) => parseInt(String(id)));

    let preselectedProducts: any[] = [];
    try {
      preselectedProducts = (content.product_ids || [])
        .map((id) => parseInt(id))
        .map((idNum) => this.adminService.productMap[idNum])
        .filter((p) => !!p);
    } catch {}

    return { productId, selectedProductIds, preselectedProducts };
  }

  private normalizeStringBeta(value: any): string | null {
    if (value === true || `${value}` === "1") return "public";
    if (typeof value === "string" && ["public", "private", "internal"].includes(value)) return value;
    return null;
  }

  private isBetaValue(value: any): boolean {
    if (value === true || `${value}` === "1") return true;
    if (typeof value === "string" && ["public", "private", "internal"].includes(value)) return true;
    return false;
  }

  private parseBooleanValue(value: any): boolean {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      return value.toLowerCase() === "true" || value === "1";
    }
    return false;
  }

  private handleContentPackageSubmit(result: any): void {
    this.clearContentPackagesCache();
    this.loadContentPackages();
  }

  deleteContentPackage(content: ContentPackage, event?: any): void {
    if (event) {
      event.stopPropagation();
    }

    // Use the AdminService method to get product names
    this.adminService.getProductNames(content.product_ids || [], this.destroy$).subscribe((productNames: string[]) => {
      this.showDeleteConfirmation(content, productNames);
    });
  }

  private showDeleteConfirmation(content: ContentPackage, productNames: string[]): void {
    let confirmationMessage = `${this.translate.instant("content_packages.confirm_delete")} "${content.title}"?`;

    if (productNames.length > 0) {
      confirmationMessage += `<br><br><strong>${this.translate.instant("content_packages.delete_warning_associated_products")}</strong><br>`;
      productNames.forEach((name) => {
        confirmationMessage += `• ${name}<br>`;
      });
      confirmationMessage += `<br><span class="text-warning">${this.translate.instant("content_packages.delete_warning_removal")}</span>`;
    }

    let modalRef = this.appService.showModal(GenericAlertComponent, { size: "lg" });
    modalRef.componentInstance.html = confirmationMessage;
    modalRef.componentInstance.submitText = this.translate.instant("content_packages.confirm");
    modalRef.componentInstance.submitFn = () => {
      this.productContentService.showLoading();

      const contentData = this.productContentService.buildContentData(content);
      const removedIds = (content.product_ids || []).map((id) => parseInt(String(id)));
      this.productContentService.removeProductAssociation(contentData, removedIds).subscribe({
        next: () => {
          this.productContentService.hideLoading();
          this.clearContentPackagesCache();
          this.loadContentPackages();
          this.productContentService.showSuccessMessage(this.translate.instant("content_packages.delete_success"));
        },
        error: (error) => {
          this.productContentService.hideLoading();
          this.productContentService.handleError(error, "deleting content package");
        },
      });
    };
  }

  getProducts(type: string, force?: boolean): void {
    if (this.cachedProducts[type].length && !force) {
      return;
    }

    this.loading = true;
    if (force) {
      delete this.adminService.productTypeMap[type];
      delete this.cachedProducts[type];
    }

    this.adminService.productList(type, true, force).subscribe({
      next: (products: Array<SimpleProduct>) => {
        this.loading = false;
        this.cachedProducts[type] = products;

        // Build product map for content package associations
        products.forEach((item: any) => {
          this.adminService.productMap[item.id] = item;
        });
      },
      error: (error) => {
        this.loading = false;
        console.error(`Error loading ${type} products:`, error);
        this.appService.alertError(error);
      },
    });
  }

  filterList(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    this.onSearchChange(target.value);
  }

  clear(): void {
    this.onSearchChange("");
  }

  resetFilters(): void {
    this.onClearFilters();
  }

  // Helper method to check if a filter has a selected value (not "any")
  hasSelectedValue(filterKey: string): boolean {
    const value = this.filters[filterKey];
    return value && value !== "any";
  }

  // Helper method to get the count of active filters
  getActiveFilterCount(): number {
    let count = 0;

    // Count non-default filter values
    if (this.filters.type !== "any") count++;
    if (this.filters.beta !== "any") count++;
    if (this.filters.downloadSection !== "any") count++;
    if (this.filters.folder !== "any") count++;
    if (this.filters.versionType !== "any") count++;
    if (this.filters.query && this.filters.query.trim() !== "") count++;

    return count;
  }

  // Helper method to check if any filters are active
  hasActiveFilters(): boolean {
    return this.getActiveFilterCount() > 0;
  }

  // Helper method to get filter label for display
  getFilterLabel(filterKey: string, value: string): string {
    const config = this.filterConfigs.find((c) => c.key === filterKey);
    if (config) {
      const option = config.options.find((o) => o.value === value);
      return option ? option.label : value;
    }
    return value;
  }
}
